import {Coder} from '../services/Coder';
import {ShowroomFrame} from './ShowroomFrame';

let showroomInstance: ShowroomFrame = null;

/**
 * Facade object to expose interactions with a "hosted" MyWebsite Showroom.
 */
class IntegratorApi {

    /**
     * Convenience method. Encodes a JS object to Base64.
     *
     * @param rawObject
     */
    public encodeObject(rawObject: any) {
        return (new Coder).encode(rawObject);
    }

    /**
     * Convenience method. Decodes a Base64 string to a JS object.
     *
     * @param encodedData
     * @returns {Object}
     */
    public decodeObject(encodedData: string) {
        return (new Coder).decode(encodedData);
    }

    /**
     * Initializes a Showroom instance inside a container element in the page.
     *
     * @param container - Usually a DIV of the desired size.
     *     The Showroom will span the full size of the supplied element and
     *     will have scrollbars if it needs to overflow.
     * @param baseShowroomUrl - the Showroom instance to interact with
     * @param locale - the language in which to localize the Showroom
     * @param host - The name of the host system. Ex.: "shop"
     * @param config (optional) - Contains the Init Configuration of the Showroom.
     *     Equivalent to the initConfig URL parameter that the Showroom may accept.
     *
     * @throws Error
     */
    public init_1(container: HTMLElement, baseShowroomUrl: string, locale: string,
        host: string, config: any) {

        // enforce only one init //
        if (showroomInstance !== null) {
            throw new Error('init was already called before');
        }
        let iframeStyle = <CSSStyleDeclaration>{
            height: '0'
        };
        const iframe = this.prepareContainerAndIframe(container, iframeStyle);

        // instantiate a Showroom object on the iframe //
        showroomInstance = new ShowroomFrame(
            iframe,
            baseShowroomUrl +
                '?locale=' + locale +
                '&host=' + host +
                '&initConfig=' + this.encodeObject(config)
        );
        showroomInstance.load();
    }

    public init_2(container: HTMLElement, baseShowroomUrl: string, locale: string, config: any) {

        // enforce only one init //
        if (showroomInstance !== null) {
            throw new Error('init was already called before');
        }

        let iframeStyle = <CSSStyleDeclaration>{};
        if (config.hasOwnProperty('host')) {
            switch (config.host) {
                case 'diyeditor':
                    iframeStyle.height = '100%';
                    iframeStyle.position = 'relative';
                break;
                default:
                    iframeStyle.height = '0';
                break;
            }
        }
        const iframe = this.prepareContainerAndIframe(container, iframeStyle);

        // instantiate a Showroom object on the iframe //
        showroomInstance = new ShowroomFrame(
            iframe,
            this.makeFrameUrl(baseShowroomUrl, locale, config)
        );
        showroomInstance.load();
    }

    public init() {
        this.init_1.apply(this, arguments);

    }

    /**
     * Adds a callback to be called when the client successfully selects something
     * in the Showroom.
     *
     * @param callback (selectionData) - Will be called after the user confirms
     *     a final selection.
     *     @param {Object} selectionData - Object map containing what was selected
     *         by the client in the Showroom. Keys:
     *         - "selectionId" - What selection (design) the client selected.
     *         - "topicId" (optional) - What topic the client selected (or was automatically
     *             chosed by the Showroom based on the selection)
     */
    public registerSelectCallback(callback: Function) {
        if (showroomInstance === null) {
            throw new Error('no showroom instance is initialized');
        }

        showroomInstance.addSelectCallback(callback);
    }

    /**
     * Adds a callback to be called when the Showroom fails.
     *
     * @param callback - No indication of what the specific failure was will
     *     be given.
     */
    public registerErrorCallback(callback: Function) {
        if (showroomInstance === null) {
            throw new Error('no showroom instance is initialized');
        }

        showroomInstance.addErrorCallback(callback);
    }

    /**
     * Adds a callback to be called when the showroom needs an overlay to show a popup
     *
     * @param {Function} callback (overlayData)
     *     @param {Object} overlayData - Object map containing overlay properties. Keys:
     *         - "color" - overlay color hex coder
     *         - "opacity" - overlay opacity
     */
    public registerOpenOverlayCallback(callback: Function) {
        if (showroomInstance === null) {
            throw new Error('no showroom instance is initialized');
        }

        showroomInstance.addOpenOverlayCallback(callback);
    }

    /**
     * Adds a callback to be called when the showroom needs the overlay to close
     *
     * @param {Function} callback ()
     */
    public registerCloseOverlayCallback(callback: Function) {
        if (showroomInstance === null) {
            throw new Error('no showroom instance is initialized');
        }

        showroomInstance.addCloseOverlayCallback(callback);
    }

    /**
     * Checks if the client browser will be able to run the Showroom.
     */
    public isBrowserSupported() {
        // We already know it's supported because we checked in the bootstrap
        // and provided a mock object otherwise
        return true;
    }

    /**
     * Changes the showroom content inside the iframe.
     *
     * This is currently used for development, to create PETSI websites where
     * showroom is inside iframe (shop mode).
     *
     * @param baseShowroomUrl
     * @param locale
     * @param config
     */
    public changeFrameUrl(baseShowroomUrl: string, locale: string, config: any) {
        if (showroomInstance) {
            showroomInstance.setFrameUrl( this.makeFrameUrl(baseShowroomUrl, locale, config) );
        }
    }

    private emptyContainer(container: HTMLElement) {
        while (container.lastChild) {
            container.removeChild(container.lastChild);
        }
    }

    private createShowroomIframe(iframeStyle: CSSStyleDeclaration): HTMLIFrameElement {
        let iframe = document.createElement("iframe");
        iframe.style.padding = 'none';
        iframe.style.border = 'none';
        iframe.style.width = '100%';
        iframe.style.height = '0';
        for (let prop in iframeStyle) {
            if (iframeStyle.hasOwnProperty(prop)) {
                iframe.style[prop] = iframeStyle[prop];
            }
        }
        return <HTMLIFrameElement>iframe;
    }

    private prepareContainerAndIframe(container: HTMLElement, iframeStyle: CSSStyleDeclaration): HTMLIFrameElement {
        // set the content of the container to be an iframe //
        this.emptyContainer(container);
        let iframe = this.createShowroomIframe(iframeStyle);
        container.appendChild(iframe);
        return iframe;
    }

    private makeFrameUrl(baseShowroomUrl: string, locale: string, config: any) {
        return baseShowroomUrl +
            (baseShowroomUrl.indexOf("?") !== -1 ? "&" : "?") + 'locale=' + locale +
            '&initConfig=' + this.encodeObject(config);
    }
}

export const integratorApi = new IntegratorApi;



// WEBPACK FOOTER //
// /build/~/tslint-loader!./host/integratorApi.ts